import {
    Api,
    CurrencySettings, CurrencyUtil, ModelRow,
    ProcessLock,
    StringUtil
} from "@mcleod/core";
import {
    BlurEvent,
    Button, ChangeEvent, Checkbox, ClickEvent, DataSourceMode, Label, Layout, SlideoutDecorator, TableRow, TableRowDisplayEvent, Textbox, Image,
    LookupModelSelectionEvent,
    DropdownSelectionEvent,
    SelectionType,
    TableSelectionEvent,
    DataSource,
    StepEvent,
    LookupModelSearchEvent,
    Step,
    Snackbar,
    TableContentsChangedEvent,
    TableAction,
    DataDisplayEvent,
} from "@mcleod/components";
import { AutogenLayoutMultiCarrierAssignmentStepper } from "./autogen/AutogenLayoutMultiCarrierAssignmentStepper";
import { ContactVerificationUtil } from "./ContactVerificationUtil";
import { CarrierAssignment } from "./CarrierAssignment";
import { CommonTooltips } from "@mcleod/common";
import { MultiSendRateConfirmation } from "@mcleod/dispatch/src/planning/MultiSendRateConfirmation";
import { MultiSendLoadTender } from "@mcleod/dispatch/src/MultiSendLoadTender";
import { MultiLoadTenderResults } from "@mcleod/dispatch/src/MultiLoadTenderResults";
import { MultiAssignCarrierQual } from "./MultiAssignCarrierQual";

const CARRIER_PAY_TYPES = {
    "D": "Distance",
    "C": "CWT",
    "F": "Flat",
    "T": "Tons",
    "E": "Per Piece",
}

enum MultiAssignStep {
    ASSIGN_CARRIER = 0,
    SEND_RATE_CON = 1
}

export class MultiCarrierAssignmentStepper extends AutogenLayoutMultiCarrierAssignmentStepper {
    assignSelectedButton: Button;
    sendRateNowButton: Button;
    sendRateLaterButton: Button;
    sendTenderNowButton: Button;
    sendTenderLaterButton: Button;
    movementOverride: any[];
    layoutMultiSendRateConfirmation: MultiSendRateConfirmation;
    layoutMultiSendLoadTender: MultiSendLoadTender;
    layoutMultiLoadTenderResults: MultiLoadTenderResults;
    stepSendRateCon: Step;
    stepTenderResults: Step;
    stepSendTender: Step;

    override onLoad(): void {
        this.setupSteps();
        this.textboxCurrencyCodeSelector.visible = CurrencySettings.getSingleton().multiCurrencyEnabled();
        CurrencyUtil.configureCurrencySelector(this.textboxCurrencyCodeSelector);
        this.textboxOverrideType.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
            this.calculatePay();
        });
        this.layoutTableExtraPay.tableExtraPay.addDataDisplayListener((event: DataDisplayEvent) => {
            //Added to calculate pay when the extra pay row is deleted
            this.calculatePay();
        });
        this.layoutTableExtraPay.calculatePay = (event: TableContentsChangedEvent) =>  {
            this.calculatePay(!(TableAction.DELETE == event.getAction()));
        };
        this.layoutTableExtraPay.tableExtraPay.persistChangesImmediately = false;
    }

    removeStepClickListeners(step: Step) {
        const clickListenersLabel = Object.assign([], step["_headingLabel"].getListeners("_clickListeners")?.listeners);
        for (const listener of clickListenersLabel) {
            step["_headingLabel"].removeClickListener(listener);
        }
        const numberLabelClicks = Object.assign([], step["_heading"].components[0]?.getListeners("_clickListeners")?.listeners);
        for (const listener of numberLabelClicks) {
            step["_heading"].components[0].removeClickListener(listener);
        }
    }

    setupSteps() {
        this.stepSendRateCon = new Step(null);
        this.removeStepClickListeners(this.stepSendRateCon);
        this.stepSendRateCon.caption = "Send Rate Con"
        this.layoutMultiSendRateConfirmation = Layout.getLayout("lme/dispatch/planning/MultiSendRateConfirmation") as MultiSendRateConfirmation;
        this.layoutMultiSendRateConfirmation.sendSelectedButton = this.sendRateNowButton;
        this.stepSendRateCon.add(this.layoutMultiSendRateConfirmation)


        this.stepSendTender = new Step(null);
        this.removeStepClickListeners(this.stepSendTender);
        this.stepSendTender.caption = "Send Tenders";
        this.layoutMultiSendLoadTender = Layout.getLayout("lme/dispatch/MultiSendLoadTender") as MultiSendLoadTender;
        this.layoutMultiSendLoadTender.sendSelectedButton = this.sendTenderNowButton;
        this.stepSendTender.add(this.layoutMultiSendLoadTender)

        this.stepTenderResults = new Step(null);
        this.removeStepClickListeners(this.stepTenderResults);
        this.stepTenderResults.caption = "Tender Results";
        this.layoutMultiLoadTenderResults = Layout.getLayout("lme/dispatch/MultiLoadTenderResults") as MultiLoadTenderResults;
        this.stepTenderResults.add(this.layoutMultiLoadTenderResults)

        this.assignSelectedButton.visible = true;
    }

    static async showAsSlideout(selectedRows: TableRow[], buttonAssign: Button) {
        const saveButton = new Button({
            id: "buttonAssignCarrier",
            caption: "Assign",
            onClick: async (event: ClickEvent) => {
                saveButton.busy = true;
                const rowsToPost = layout.tableMultiAssignment.selectedRows.map(row => {
                    return {
                        ...row.data.data
                    };
                });
                const extraPayRows = layout.layoutTableExtraPay.tableExtraPay.allRows.map(row => {
                    return {
                        ...row.data.data
                    };
                });
                const payee_id = layout.mainDataSource.activeRow.get("pending_payee_id")
                const response = await Api.post("lme/powerbroker/multi-assign-carrier", { movements: rowsToPost, payee_id: payee_id, extra_pay_rows: extraPayRows });
                saveButton.busy = false;
                const nextIndex = layout.stepperMultiAssignment.selectedIndex + 1;
                layout.stepperMultiAssignment.selectedIndex = nextIndex;
                if (layout.stepperMultiAssignment.components[1] == layout.stepSendRateCon) {
                    layout.layoutMultiSendRateConfirmation.searchMovementsRateConfirmation(response?.data);
                }
                else if (layout.stepperMultiAssignment.components[1] == layout.stepSendTender) {
                    layout.layoutMultiSendLoadTender.sourceSendTenders = layout.layoutMultiLoadTenderResults.mainDataSource;
                    layout.layoutMultiSendLoadTender.previewLoadTenders(response?.data, payee_id);
                }
                else {
                    layout.layoutMultiLoadTenderResults.autoSendLoadTenders(response?.data, payee_id);
                }

                selectedRows[0].table.selectedRows = [];
            },
            backgroundColor: "primary",
            color: "primary.reverse",
            minWidth: 128,
            borderWidth: 0,
            rowBreak: false
        })

        const sendRateNow = new Button({
            id: "buttonSendNow",
            caption: "Send Now",
            onClick: async (event: ClickEvent) => {
                layout.layoutMultiSendRateConfirmation.buttonSendSelectedOnClick(event);
            },
            backgroundColor: "primary",
            visible: false,
            color: "primary.reverse",
            minWidth: 128,
            borderWidth: 0,
            rowBreak: false
        })

        const sendRateLater = new Button({
            id: "buttonSendLater",
            caption: "Send Later",
            onClick: async (event: ClickEvent) => {
                layout.slideOut();
            },
            borderWidth: 1,
            borderColor: "strokeSecondary",
            backgroundColor: "primary.darker",
            color: "strokeSecondary",
            visible: false,
            minWidth: 128,
            rowBreak: false
        })

        const sendTenderNow = new Button({
            id: "buttonSendNow",
            caption: "Send Now",
            onClick: async (event: ClickEvent) => {
                layout.layoutMultiSendLoadTender.buttonSendSelectedTendersOnClick(event);
                const nextIndex = layout.stepperMultiAssignment.selectedIndex + 1;
                layout.stepperMultiAssignment.selectedIndex = nextIndex;
            },
            backgroundColor: "primary",
            visible: false,
            color: "primary.reverse",
            minWidth: 128,
            borderWidth: 0,
            rowBreak: false
        })

        const sendTenderLater = new Button({
            id: "buttonSendLater",
            caption: "Send Later",
            onClick: async (event: ClickEvent) => {
                layout.slideOut();
            },
            borderWidth: 1,
            borderColor: "strokeSecondary",
            backgroundColor: "primary.darker",
            color: "strokeSecondary",
            visible: false,
            minWidth: 128,
            rowBreak: false
        })

        const movements = [];
        const movementIds = [];
        for (const row of selectedRows) {
            const modelRow = row.data as ModelRow;
            movementIds.push(modelRow.get("movement_id"));
            movements.push({
                ...modelRow.data
            });
        }

        const lockResults = await ProcessLock.acquireLocks("Carrier dispatch", movementIds);
        buttonAssign.busy = false;

        const layout = Layout.getLayout("lme/powerbroker/MultiCarrierAssignmentStepper") as MultiCarrierAssignmentStepper;
        layout.assignSelectedButton = saveButton;
        layout.sendRateLaterButton = sendRateLater;
        layout.sendRateNowButton = sendRateNow;
        layout.sendTenderLaterButton = sendTenderLater;
        layout.sendTenderNowButton = sendTenderNow;
        return new SlideoutDecorator({
            title: "Multi-Select Assign Carrier",
            layout: layout,
            width: "85%",
            fillVerticalSpace: true,
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                layout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await layout.mainDataSource.createBlankRow()]);
                for (const id of lockResults.failedKeys) {
                    const index = movements.findIndex(row => row.movement_id == id);
                    if (index > -1) {
                        movements.splice(index, 1);
                    }
                    const index2 = movementIds.findIndex(row => row == id);
                    if (index2 > -1) {
                        movementIds.splice(index2, 1);
                    }
                }
                await layout.sourceMultiCarrierValidator.search({ movements });
                if (!StringUtil.isEmptyString(lockResults.message)) {
                    Snackbar.showWarningSnackbar(lockResults.message);
                }
                layout.updateTitleNumbers();
                layout.sortTableMultiAssignment(layout.sourceMultiCarrierValidator);
                layout.layoutMultiSendLoadTender.sendSelectedButton = sendTenderNow;
                layout.movementOverride = movements.map(row => {
                    return {
                        ...row
                    };
                });
            },
            doAfterSlideOut: () => {
                if (movementIds.length > 0) {
                    ProcessLock.releaseLocks("Carrier dispatch", movementIds);
                }
            },
            onClose: async (cancelled: boolean) => {
                layout.slideOut();
            },
            addlComponents: [saveButton, sendRateLater, sendRateNow, sendTenderLater, sendTenderNow]
        });
    }

    stepperMultiAssignmentBeforeStepListener(event: StepEvent) {
        //dont allow to go backwards
        if (event.newIndex < event.oldIndex) {
            event.preventDefault();
            return;
        }

        if (this.stepperMultiAssignment.components[event.newIndex] == this.stepSendRateCon) {
            this.sendRateNowButton.visible = true;
            this.sendRateLaterButton.visible = true;
            this.assignSelectedButton.visible = false;
        }
        else if (this.stepperMultiAssignment.components[event.newIndex] == this.stepSendTender) {
            this.assignSelectedButton.visible = false;
            this.sendTenderNowButton.visible = true;
            this.sendTenderLaterButton.visible = true;
        }
        else {
            this.assignSelectedButton.visible = false;
            this.sendTenderNowButton.visible = false;
            this.sendTenderLaterButton.visible = false;
        }
    }

    textboxContactinitalBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.parent_row_id = this.mainDataSource.activeRow?.get("pending_payee_id");
    }

    tableMultiAssignmentOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        const checkbox = tableRow.findComponentById("checkboxSelectRow") as Checkbox;
        const labelAutoRated = tableRow.findComponentById("labelAutoRated") as Label;
        const rateId = modelRow.get("carrier_rate_id")
        if (labelAutoRated && rateId) {
            labelAutoRated.visible = true;
            CommonTooltips.setTooltipContentLayout(
                labelAutoRated,
                rateId,
                "lme/powerbroker/CarrierRateQuickInfo"
            );
        }
        const textboxPayMethod = tableRow.findComponentById("textboxPayMethod") as Textbox;
        if (textboxPayMethod) {
            textboxPayMethod.text = CARRIER_PAY_TYPES[modelRow.get("type")];
        }
        if (!StringUtil.isEmptyString(modelRow.getString("dispatch_warnings", null))) {
            const imageWarning = tableRow.findComponentById("imageWarning") as Image;
            if (imageWarning) {
                imageWarning.visible = true;
                imageWarning.tooltipDynamicContent = async () => {
                    return await this.makeQualResultTooltip(modelRow, "dispatch_warnings", "Dispatch Warnings");
                }
            }
        }
        if (modelRow.get("qualified") == false) {
            const imageDisqualified = tableRow.findComponentById("imageError") as Image;
            checkbox.enabled = false;
            tableRow.enabled = false;
            if (imageDisqualified) {
                imageDisqualified.visible = true;
                imageDisqualified.tooltipDynamicContent = async () => {
                    return await this.makeQualResultTooltip(modelRow, "dispatch_errors");
                }
            }
        } else {
            this.tableMultiAssignment.selectRow(tableRow, SelectionType.DISTINCT, null);
        }
        this.updateTitleNumbers();
    }

    tableMultiAssignmentOnSelect(event: TableSelectionEvent) {
        this.updateTitleNumbers();
    }

    private async makeQualResultTooltip(modelRow: ModelRow, resultField: string, altTitle?: string): Promise<Layout> {
        const layout = await Layout.getLoadedLayout("lme/powerbroker/MultiAssignCarrierQual") as MultiAssignCarrierQual;
        if (altTitle) {
            layout.labelMissing.text = altTitle;
        }
        const dispatchErrors = modelRow.get(resultField);
        if (dispatchErrors) {
            const errors = dispatchErrors.split("\n");
            errors.forEach(error => {
                const label = new Label({
                    text: error,
                    fontBold: true,
                    rowBreak: true
                });
                layout.add(label);
            });
        }
        return layout;
    }

    async textboxCarrierInitalOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.stepperMultiAssignment["panelHeaderContent"].remove(this.stepperMultiAssignment["panelHeaderContent"].findComponentById("panelConnector" + this.stepSendTender.index));
        this.stepperMultiAssignment["panelHeaderContent"].remove(this.stepperMultiAssignment["panelHeaderContent"].findComponentById("panelConnector" + this.stepTenderResults.index));
        this.stepperMultiAssignment["panelHeaderContent"].remove(this.stepperMultiAssignment["panelHeaderContent"].findComponentById("panelConnector" + this.stepSendRateCon.index));
        this.stepperMultiAssignment.remove(this.stepSendTender);
        this.stepperMultiAssignment.remove(this.stepTenderResults);
        this.stepperMultiAssignment.remove(this.stepSendRateCon);
        this.setupSteps();
        const selectedRow = event.selection;
        this.activeRow.setValues({
            "carrier_contact": selectedRow?.get("contact.name"),
            "carrier_phone": selectedRow?.get("contact.phone"),
            "carrier_email": selectedRow?.get("contact.email")
        });
        const row = selectedRow?.getFirstLookupModelData("carrier_contact");
        if (selectedRow) {
            this.sourceCarriersForAssignment.setRowsAndMode(DataSourceMode.NONE, [selectedRow]);
        }
        this.setStarRatingVisibility(!!selectedRow);
        ContactVerificationUtil.updateVerificationImage(row, this.imageCarrierVerification);
        this.layoutTableExtraPay.payeeId = this.activeRow?.get("pending_payee_id", null);
        this.activeRow.setLookupModelData("carrier_contact", { "name": selectedRow?.get("contact.name") });
        this.mainDataSource.displayDataInBoundComponents();
        const movements = this.tableMultiAssignment.rows.map(row => row.data);
        const movementsToSearch = movements.length > 0 ? movements : this.movementOverride;
        this.sourceMultiCarrierValidator.search({
            movements: movementsToSearch, payee_id: this.mainDataSource.activeRow.get("pending_payee_id"),
            pay_method: this.mainDataSource.activeRow.get("override_type"), pay_rate: this.mainDataSource.activeRow.get("override_pay_rate"),
            extra_pay_rows: this.layoutTableExtraPay.tableExtraPay.allRows.map(row => row.data), carrier_contact: this.mainDataSource.activeRow.get("carrier_contact"),
            carrier_phone: this.mainDataSource.activeRow.get("carrier_phone"), carrier_email: this.mainDataSource.activeRow.get("carrier_email")
        }).then(() => {
            this.sortTableMultiAssignment(this.sourceMultiCarrierValidator);
        });
        const modelRow = this.layoutTableExtraPay.tableExtraPay?.quickAddData as ModelRow;
        const payeeId = this.activeRow?.get("pending_payee_id", null);
        modelRow?.setValueAndLookupModelData("payee_id", payeeId, { id: payeeId });
        this.layoutTableExtraPay.tableExtraPay?.quickAddRow?.findComponentById("textboxPayAdjustmentsPayeeId").displayData(modelRow, null, 0);
        const dfResult = await CarrierAssignment.checkCarrierForDataFusion(payeeId)
        const isDataFusion = (dfResult.is_datafusion_carrier == "auto" || dfResult.is_datafusion_carrier == "manual")
        this.dfLabel.visible = isDataFusion;
        if (event.selection != null) {
            if (!isDataFusion) {
                this.stepperMultiAssignment.add(this.stepSendRateCon);
            }
            else if (dfResult.is_datafusion_carrier == "auto") {
                this.stepperMultiAssignment.add(this.stepTenderResults);
            }
            else {
                this.stepperMultiAssignment.add(this.stepSendTender);
                this.stepperMultiAssignment.add(this.stepTenderResults);
            }
        }
    }

    setStarRatingVisibility(override: boolean = true) {
        this.starratingScorecard.visible = override && this.sourceCarriersForAssignment.activeRow?.get("rating_star_count") != null;
    }

    textboxContactinitalOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        if (textbox.isEmpty()) {
            this.activeRow.set("carrier_phone", null);
            this.activeRow.set("carrier_email", null);
            ContactVerificationUtil.updateVerificationImage(null, this.imageCarrierVerification);
        }
    }

    textboxContactinitalOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const selectedRow = event.selection;
        this.activeRow.setValues({
            "carrier_phone": selectedRow?.get("phone"),
            "carrier_email": selectedRow?.get("email")
        });
        ContactVerificationUtil.updateVerificationImage(selectedRow, this.imageCarrierVerification);
        const payee_id = this.activeRow.get("pending_payee_id");
        const movements = this.tableMultiAssignment.rows.map(row => row.data);
        const movementsToSearch = movements.length > 0 ? movements : this.movementOverride;
        this.sourceMultiCarrierValidator.search({
            movements: movementsToSearch, payee_id: payee_id,
            pay_method: this.mainDataSource.activeRow.get("override_type"), pay_rate: this.mainDataSource.activeRow.get("override_pay_rate"),
            extra_pay_rows: this.layoutTableExtraPay.tableExtraPay.allRows.map(row => row.data), carrier_contact: this.mainDataSource.activeRow.get("carrier_contact"),
            carrier_phone: this.mainDataSource.activeRow.get("carrier_phone"), carrier_email: this.mainDataSource.activeRow.get("carrier_email")
        }).then(() => {
            this.sortTableMultiAssignment(this.sourceMultiCarrierValidator);
        });
    }

    updateTitleNumbers() {
        this.labelTotalCount.text = this.tableMultiAssignment.selectedRows.length.toString();
        this.labelErrorCount.text = this.tableMultiAssignment.rows.filter(row => row.enabled == false).length.toString();
        if (this.tableMultiAssignment.selectedRows.length == 0) {
            this.assignSelectedButton.enabled = false;
            this.assignSelectedButton.disabledTooltip = "You must select at least one movement to assign.";
        }
        else {
            this.assignSelectedButton.enabled = true;
        }
    }

    rateRelatedFieldChanged(event: BlurEvent | ChangeEvent) {
        if (event instanceof BlurEvent && event.changedWhileFocused) {
            this.calculatePay();
        }
    }

    textboxCurrencyCodeSelectorOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (this.layoutTableExtraPay) this.layoutTableExtraPay.currencySelected = event.selection?.get("currency_type_id");
        this.calculatePay();
    }

    async calculatePay(extraPayChanged = false) {
        let carrierRated = false;
        const movements = this.tableMultiAssignment.rows.map(row => {
            if (!carrierRated && !row.data.isNull("carrier_rate_id")) {
                carrierRated = true;
            }
            return row.data;
        });
        const movementsToSearch = movements.length > 0 ? movements : this.movementOverride;
        await this.sourceMultiCarrierValidator.search({
            movements: movementsToSearch, payee_id: this.mainDataSource.activeRow.get("pending_payee_id"),
            pay_method: this.mainDataSource.activeRow.get("override_type"), pay_rate: this.mainDataSource.activeRow.get("override_pay_rate", 0),
            extra_pay_rows: this.layoutTableExtraPay.tableExtraPay.allRows.map(row => row.data), carrier_contact: this.mainDataSource.activeRow.get("carrier_contact"),
            carrier_phone: this.mainDataSource.activeRow.get("carrier_phone"), carrier_email: this.mainDataSource.activeRow.get("carrier_email")
        });
        this.sortTableMultiAssignment(this.sourceMultiCarrierValidator);
        if (extraPayChanged && carrierRated) {
            Snackbar.showWarningSnackbar("Pay Adjustments cannot be added to Carrier Auto Rated Loads.");
        }
    }

    sortTableMultiAssignment(datasource: DataSource) {
        datasource.setRowsAndMode(DataSourceMode.NONE, datasource.data.sort((a, b) => {
            const aWeight = a.getBoolean("qualified") ? 1 : 0;
            const bWeight = b.getBoolean("qualified") ? 1 : 0;
            return aWeight - bWeight;
        }))
    }
}
